type ForecastRuntimeVariables = {
  ENVIRONMENT: string
  SENTRY_DSN: string
  BASE_PATH: string
  MIXPANEL_AUTH_TOKEN: string
  ACCESS_URL: string
  ROLODEX_URL: string
  ENTRY_URL: string
  AUTH0_CLIENT_ID: string
  AUTH0_AUDIENCE: string
  DATA_OPS_URL: string
  CONDUIT_API_URL: string
  MAPBOX_KEY: string
  RUNNING_ON: string
  GECKO_ASSETS_URL: string
  KESTREL_SESSION_SERVICE_URL: string
  KESTREL_API_URL: string
  COGNITO_BASE_DOMAIN: string
  COGNITO_CLIENT_ID: string
  COGNITO_USER_POOL_ID: string
  AWS_REGION: string
  KEYCLOAK_CLIENT_ID: string
  KEYCLOAK_DOMAIN: string
  KEYCLOAK_REALM: string
  RAUT_GDRIVE_ROLODEX_SYNC_URL: string
  FORECAST_VERSION: string
  SIGNAL_SERVER_URL: string
  RISK_CALCULATOR_URL: string
  CONCRETE_PROCESSOR_URL: string
  LLM_SERVICE: string
  TIME_SERIES_BASE_URL: string
  FORECAST_API_PROXY_URL: string
  ALERTS_URL: string
  SAGE_URL: string
  TIME_SERIES_SERVICE: string
  ANOMALY_DETECTION_SERVICE_URL: string
  FFS_SERVICE_URL?: string
  FIM_SERVICE_URL: string
  DHB_SERVICE_URL?: string
  OTEL_COLLECTOR_URL: string
  SENTRY_ORG: string
  POWER_BACKEND_URL: string
  POWER_BACKEND_AI_URL: string
  PROD_TO_DEV_SYNC_URL: string
  MATTERPORT_SDK_KEY: string
  GONG_AGENT_URL: string
}

/** Default Concrete Processor URL to use for all configurations. */
const CONCRETE_CLOUD_PROCESSOR_DEFAULT_URL =
  'https://concrete-data-processor.dev.cloud.geckorobotics.com'

/** Default Kestrel session service URL for Kubernetes Kestrel sessions. */
const KESTREL_SESSION_SERVICE_DEFAULT_URL =
  'https://kestrel-session.dev.cloud.geckorobotics.com'

/** Default Kestrel API url to reach in production. */
const KESTREL_API_DEFAULT_URL =
  'https://kestrel-api.dev.cloud.geckorobotics.com'

/**
 * Default runtime variables used for local dev builds. These can be overwritten
 * by local environment variables if the OVERWRITE_CONFIG=true for the process
 * running `npm run dev`
 */
export const defaultForecastRuntimeVariables: ForecastRuntimeVariables = {
  AUTH0_AUDIENCE: 'dev.cloud.geckorobotics.com',
  AUTH0_CLIENT_ID: 'Q1zGDneT5Xwt70Ke0e2i9E5aXrGZ6aXV',
  BASE_PATH: '',
  MAPBOX_KEY:
    'pk.eyJ1IjoiZ2Vja29yb2JvdGljcyIsImEiOiJjbGwycmE0dmMxaTg5M2xsczByYzdhd296In0.dhEv6ff1JBIRG_Uvq9GseA',
  DATA_OPS_URL: 'https://data-ops.dev.cloud.geckorobotics.com',
  CONDUIT_API_URL: 'https://conduit-api.dev.cloud.geckorobotics.com',
  ROLODEX_URL: 'https://rolodex.dev.cloud.geckorobotics.com',
  ENTRY_URL: 'https://entry.dev.cloud.geckorobotics.com',
  SAGE_URL: 'https://gecko-sage.dev.cloud.geckorobotics.com',
  RUNNING_ON: 'local',
  SENTRY_DSN:
    'https://b53a0e4fb64d41a4abb8bfad90e757e7@o201370.ingest.us.sentry.io/4504526572814336',
  SIGNAL_SERVER_URL:
    'https://cantilever-signal-server.dev.cloud.geckorobotics.com',
  ALERTS_URL: 'https://cantilever-alerts.dev.cloud.geckorobotics.com',
  ENVIRONMENT: 'local',
  RISK_CALCULATOR_URL: 'https://risk-calculator.dev.cloud.geckorobotics.com',
  RAUT_GDRIVE_ROLODEX_SYNC_URL:
    'https://raut-distributed-sync-trigger.dev.cloud.geckorobotics.com/',
  MIXPANEL_AUTH_TOKEN: '70c9e6f7ee71dd242a6be242930a0d0e',
  ACCESS_URL: 'https://access.dev.cloud.geckorobotics.com',
  GECKO_ASSETS_URL: 'https://assets.dev.cloud.geckorobotics.com',
  COGNITO_BASE_DOMAIN: '',
  COGNITO_CLIENT_ID: '',
  COGNITO_USER_POOL_ID: '',
  KESTREL_SESSION_SERVICE_URL: KESTREL_SESSION_SERVICE_DEFAULT_URL,
  KESTREL_API_URL: KESTREL_API_DEFAULT_URL,
  KEYCLOAK_CLIENT_ID: '',
  KEYCLOAK_DOMAIN: '',
  KEYCLOAK_REALM: '',
  AWS_REGION: 'us-east-1',
  FORECAST_VERSION: 'local-develop',
  CONCRETE_PROCESSOR_URL: CONCRETE_CLOUD_PROCESSOR_DEFAULT_URL,
  LLM_SERVICE: 'https://llm-service.dev.cloud.geckorobotics.com',
  TIME_SERIES_BASE_URL: 'https://rolodex.dev.cloud.geckorobotics.com',
  FORECAST_API_PROXY_URL:
    'https://forecast-api-proxy.dev.cloud.geckorobotics.com',
  TIME_SERIES_SERVICE:
    'https://time-series-service.dev.cloud.geckorobotics.com',
  ANOMALY_DETECTION_SERVICE_URL:
    'https://anomaly-detection-service.dev.cloud.geckorobotics.com',
  FFS_SERVICE_URL: 'https://ffs-service.dev.cloud.geckorobotics.com',
  FIM_SERVICE_URL: 'https://fim-service.dev.cloud.geckorobotics.com',
  DHB_SERVICE_URL: 'https://dhb-service.dev.cloud.geckorobotics.com',
  OTEL_COLLECTOR_URL: '',
  SENTRY_ORG: 'gecko-robotics',
  PROD_TO_DEV_SYNC_URL: '',
  // Uncomment below to enable tracing locally
  // OTEL_COLLECTOR_URL:
  //   'https://opentelemetry-collector.dev.cloud.geckorobotics.com',
  POWER_BACKEND_URL: 'https://power-backend.dev.cloud.geckorobotics.com',
  POWER_BACKEND_AI_URL: 'https://power-backend-ai.dev.cloud.geckorobotics.com',
  MATTERPORT_SDK_KEY: 's8wkkk4611w6tpxn3yseyra4a',
  GONG_AGENT_URL: 'https://gong-agent-service.dev.cloud.geckorobotics.com',
}

/**
 * Get Forecast runtime variables. For local dev builds, these will be populated
 * from `defaultForecastRuntimeVariables`. For cloud builds, these must be
 * populated as environment variables to the running container.
 */
export const getForecastRuntimeVariables = (): ForecastRuntimeVariables => {
  const configRawText =
    document.getElementById('config-vars-embed')?.textContent
  if (!configRawText) throw new Error('Error getting Forecast config')
  return JSON.parse(configRawText) as ForecastRuntimeVariables
}
